/*
 * Universal-mode compatibility shim.
 *
 * The codebase was originally a Shopify embedded app, so it calls the global
 * `shopify.*` API that Shopify App Bridge injects (toasts, save bar, etc.).
 * In standalone "universal" mode there is no App Bridge, so that global is
 * undefined and every `shopify.toast.show(...)` call throws a ReferenceError.
 *
 * This module installs a lightweight `window.shopify` with the pieces the app
 * actually uses — primarily `toast.show(message, { error })` — implemented with
 * plain DOM so it works without any React context or Polaris Frame.
 *
 * Importing this module for its side effect (see main.jsx) is enough.
 */

const CONTAINER_ID = 'universal-toast-container';

function getContainer() {
  if (typeof document === 'undefined') return null;
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    Object.assign(container.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: '16px',
      pointerEvents: 'none',
      zIndex: '100000',
    });
    document.body.appendChild(container);
  }
  return container;
}

function showToast(message, options = {}) {
  const container = getContainer();
  if (!container) return;

  const isError = !!(options && (options.error || options.isError));
  const duration = (options && options.duration) || 4000;

  const el = document.createElement('div');
  Object.assign(el.style, {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 12px 12px 20px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    color: '#ffffff',
    background: isError ? '#d72c0d' : '#202223',
    maxWidth: '90vw',
    transform: 'translateY(8px)',
    opacity: '0',
    transition: 'opacity 0.25s ease, transform 0.25s ease',
  });

  const text = document.createElement('span');
  text.textContent = typeof message === 'string' ? message : String(message ?? '');
  el.appendChild(text);

  // Manual dismiss (×) button
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Dismiss');
  closeBtn.innerHTML = '&times;';
  Object.assign(closeBtn.style, {
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    margin: '0',
    padding: '0',
    width: '22px',
    height: '22px',
    borderRadius: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    lineHeight: '1',
    color: '#ffffff',
    opacity: '0.75',
    flexShrink: '0',
    transition: 'opacity 0.15s ease, background 0.15s ease',
  });
  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.opacity = '1';
    closeBtn.style.background = 'rgba(255,255,255,0.16)';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.opacity = '0.75';
    closeBtn.style.background = 'transparent';
  });
  el.appendChild(closeBtn);

  let dismissed = false;
  let hideTimer;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    clearTimeout(hideTimer);
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => el.remove(), 250);
  };
  closeBtn.addEventListener('click', dismiss);

  container.appendChild(el);
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  });

  hideTimer = setTimeout(dismiss, duration);
}

if (typeof window !== 'undefined') {
  window.shopify = window.shopify || {};
  window.shopify.toast = window.shopify.toast || { show: showToast, hide: () => {} };
  // No-op stubs for other App Bridge globals the legacy code may reference.
  window.shopify.saveBar = window.shopify.saveBar || {
    show: () => {},
    hide: () => {},
    leaveConfirmation: () => Promise.resolve(),
  };
  window.shopify.loading = window.shopify.loading || (() => {});
}

export { showToast };
